import React, {useCallback, useState} from 'react';

import {Analytics} from '../../../services/analytics';
import {type DrawerContentType} from '../WorkspaceDrawerContextProvider';
import {BackButton} from './components/BackButton';
import {useWorkspaceSettingOverrides} from './hooks/useSettingOverrides';
import {WorkspaceMediaSettingsDrawer} from './media/WorkspaceMediaSettingsDrawer';
import {LinePlotSettings} from './runLinePlots/LinePlotSettings';
import {LinePlotSettingsContextProvider} from './runLinePlots/LinePlotSettingsContextProvider';
import {RunsSettings} from './RunsSettings';
import {type SelectedSettingType} from './types';
import {WorkspaceLayoutSettings} from './workspaceLayout/WorkspaceLayoutSettings';
import {useWorkspaceSettingsContext} from './WorkspaceSettingsContextProvider';
import {SettingsMenu} from './WorkspaceSettingsMenu';

export const WorkspaceSettingsContent = ({
  displayedContentType,
}: {
  displayedContentType: DrawerContentType;
}) => {
  const {
    linePlot,
    trackWorkspaceSettingChange,
    updateLinePlotWorkspaceSettings,
  } = useWorkspaceSettingsContext();
  const {clearSetting, settingOverrideCounts} = useWorkspaceSettingOverrides();

  const [selectedSettingType, setSelectedSettingType] = useState<
    SelectedSettingType | undefined
  >();
  const setSelectedSettingTypeAndLog = useCallback(
    (settingType: SelectedSettingType | undefined) => {
      setSelectedSettingType(settingType);
      Analytics.track('Select Workspace Setting Menu Item', {
        settingType,
      });
    },
    [setSelectedSettingType]
  );

  if (selectedSettingType == null) {
    return (
      <SettingsMenu setSelectedSettingType={setSelectedSettingTypeAndLog} />
    );
  }

  const contentHeader = (
    <>
      <BackButton onClick={() => setSelectedSettingTypeAndLog(undefined)} />
      <p className="uppercase font-semibold text-wb-text-secondary">
        Workspace settings
      </p>
      <div className="grow" />
    </>
  );

  if (selectedSettingType === 'workspace-layout') {
    return <WorkspaceLayoutSettings contentHeader={contentHeader} />;
  }

  if (selectedSettingType === 'line-plot') {
    return (
      <LinePlotSettingsContextProvider
        clearSetting={clearSetting}
        displayLinePlotSettings={linePlot}
        settingLevel="workspace"
        settingOverrideCounts={settingOverrideCounts}
        trackLinePlotSetting={trackWorkspaceSettingChange}
        updateLinePlotSettings={updateLinePlotWorkspaceSettings}>
        <LinePlotSettings
          contentHeader={contentHeader}
          displayedContentType={displayedContentType}
        />
      </LinePlotSettingsContextProvider>
    );
  }

  if (selectedSettingType === 'runs') {
    return <RunsSettings contentHeader={contentHeader} />;
  }

  if (selectedSettingType === 'media') {
    return <WorkspaceMediaSettingsDrawer header={contentHeader} />;
  }

  return null;
};
